/**
 * Main entry point for wp-custom TypeScript modules
 * Initializes all components when document is ready
 */

import { formatNumber, formatPrice } from "./utils/formatters.js";
import { showCustomPopup } from "./components/popup.js";
import {
  initThumbnailScrolling,
  reinitThumbnailScrolling,
} from "./components/thumbnailScroll.js";
import { initDragToScroll } from "./components/dragToScroll.js";
import { initZipCodeHandler, loadTransitTimes } from "./handlers/zipCodeInput.js";
import { waitForWarehouseData, getWarehouseByCode } from "./utils/warehouseUtils.js";
import {
  initPricingGrid,
  prePopulateFromCart,
  applyUniformUpdateCartButtonHeight,
  applyUniformProductImageHeight,
} from "./components/pricingGrid.js";
import { initInputValidation } from "./handlers/inputValidation.js";
import { initAddToCartHandler } from "./handlers/addToCart.js";
import { scrollToVariantFromUrl } from "./handlers/scrollToVariant.js";
import { initColorFilter } from "./components/colorFilter.js";
import { initAvailabilityFilter, reapplyCurrentFilter } from "./components/availabilityFilter.js";
import { initSwatchGallery } from "./components/swatchGallery.js";
import { initResetFilters } from "./handlers/resetFilters.js";
import { initSingleColorMode, markCartPrepopulatedRows, activateInitialSingleColorMode, isSingleColorModeActive } from "./components/singleColorMode.js";
import { initLazyProductLoader, ensureProductLoaded } from "./components/lazyProductLoader.js";
import { initColorPin, getDeepLinkedHandle, pinColorToTop } from "./components/colorPin.js";
import { captureBaseline, setBaselineForKeys, updateButtonStates } from "./components/dirtyTracking.js";
import { initNavigationGuard } from "./handlers/navigationGuard.js";
import { initMobileCardSubmit, refreshMobileCardSubmit } from "./components/mobileCardSubmit.js";
import { initMobileMirror, syncAllMirrorsFromCanonical } from "./components/mobileMirror.js";
import { initBackToTopHandler } from "./handlers/backToTop.js";
import { snapshotQuantityValues } from "./services/cartService.js";
import { bootCartSync } from "./services/cartSync/boot.js";
import { registerAfterApplyHook } from "./services/cartSync/syncService.js";
import { initProductLightbox } from "./components/productLightbox.js";
import { initPromotions, promotionCartChanged, runPromotionWatcher } from "./components/promotionPanel.js";

// Expose utilities globally for use in Liquid templates and inline scripts
window.showCustomPopup = showCustomPopup;
window.waitForWarehouseData = waitForWarehouseData;
window.formatNumber = formatNumber;
window.formatPrice = formatPrice;
window.getWarehouseByCode = getWarehouseByCode;
// The cart page's checkout handoff re-judges a free promotion item; its trash button
// also re-asks whether the promotion box shows.
window.runPromotionWatcher = runPromotionWatcher;
window.promotionCartChanged = promotionCartChanged;

interface WarehouseInfoDetails {
  warehouseName: string;
  orderBy: string;
  delivery: string;
  daysText: string;
}

function getWarehouseInfoDetails(button: HTMLElement): WarehouseInfoDetails {
  const $button = $(button);
  const $row = $button.closest(".warehouse_row");

  if ($row.length > 0) {
    const $transitInfo = $row.find(".transit-info");
    return {
      warehouseName: $row.attr("data-warehouse-name") ?? "",
      orderBy: ($transitInfo.data("transit-order-by") as string) ?? "",
      delivery: ($transitInfo.data("transit-delivery") as string) ?? "",
      daysText: ($transitInfo.data("transit-days-text") as string) ?? "",
    };
  }

  // v4: warehouse info button now lives in the transposed table's column
  // <th class="mit-warehouse-col">. Old layout used .mobile-inventory-warehouse
  // row groups; the .closest() selector below covers both for safety until
  // the legacy lazy-loader fallback renderer is updated to the new layout.
  const warehouseHeader =
    $button.closest(".mit-warehouse-col")[0] ??
    $button.closest(".mobile-inventory-warehouse")[0];
  const warehouse = warehouseHeader as HTMLElement | undefined;
  const card = $button.closest(".mobile-inventory-product")[0] as
    | HTMLElement
    | undefined;
  const warehouseCode = warehouse?.dataset.warehouseCode ?? "";
  const productHandle = card?.dataset.productHandle ?? "";
  const $matchedRow = $(
    `.warehouse_row[data-product-handle="${productHandle}"][data-warehouse-code="${warehouseCode}"]`,
  ).first();
  const $matchedTransit = $matchedRow.find(".transit-info");

  return {
    warehouseName:
      $matchedRow.attr("data-warehouse-name") ??
      warehouse?.dataset.warehouseName ??
      "",
    orderBy: ($matchedTransit.data("transit-order-by") as string) ?? "",
    delivery: ($matchedTransit.data("transit-delivery") as string) ?? "",
    daysText: ($matchedTransit.data("transit-days-text") as string) ?? "",
  };
}

/**
 * Mark the pricing grid as ready: hide the full-screen loader, swap the
 * loading → ready class on the wrapper, and reveal the wrapper itself.
 * Called once after init completes (cart pre-pop + single-color activation
 * + uniform button height). Replaces the previous fixed 50ms setTimeout.
 */
function markProductGridReady(): void {
  $(".inventory_loader").hide();
  $(".mobile_table_wrapper.page-width")
    .removeClass("product-grid-loading")
    .addClass("product-grid-ready")
    .css("display", "flex");
}

/**
 * Pin the deep-linked color to row 1 when "Show Every Color" is checked.
 *
 * Search results and cart links arrive at `/products/{handle}?variant={id}`,
 * where the handle is the color. Single color mode handles its own row order,
 * so this only applies in show-all mode.
 *
 * No scroll, by design: search sends users into product pages on every entry,
 * and jumping the viewport down to the grid each time would be hostile. The
 * cart's scroll is separate and flagged (scrollToVariant.ts:21).
 */
async function pinDeepLinkedColor(): Promise<void> {
  if (isSingleColorModeActive()) return;

  const handle = getDeepLinkedHandle();
  if (!handle) return;

  // A Coming Soon colour has no grid rows (grid_family leaves every Coming Soon member
  // out), so there is nothing to pin and its swatch must not be outlined as if the grid
  // below were for it. scanner-dev "Style Color Active And Not Released.md" D4a.
  if ($(`.Product_swatch_colors .swatch-option[data-product_handle="${handle}"][data-coming-soon="true"]`).length > 0) return;

  // On a lazy-loaded family only the first color is server-rendered, so the
  // deep-linked one may not be in the DOM yet.
  try {
    await ensureProductLoaded(handle);
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error);
    console.error(`[wp-custom] Failed to load deep-linked color ${handle}: ${message}`);
    return;
  }

  // Move the swatch highlight too — a pinned row at the top with a different
  // swatch outlined reads as a bug.
  $(".Product_swatch_colors .swatch-option").removeClass("active-color");
  $(`.Product_swatch_colors .swatch-option[data-product_handle="${handle}"]`)
    .addClass("active-color");

  pinColorToTop(handle);
}

// Cart sync boots at module scope, NOT inside $(document).ready.
//
// It only reads window globals and calls fetch, so it needs no DOM — and the
// cart page's inline script registers its DOMContentLoaded listener before
// jQuery's ready queue drains, so booting inside ready() left
// window.__wpCartSync undefined at the moment that script looks for it and the
// restore control could never appear. Booting here also starts the first pull
// earlier, which is what the grid wants anyway.
bootCartSync({ collaborative: true });

$(document).ready(function () {
  if (navigator.platform === "MacIntel") {
    $("body").addClass("safari_browser");
  }

  // Pin the uniform product-image height as early as possible (reads only a
  // server-rendered data attribute, no warehouse data needed). Doing this
  // before the init setTimeouts below avoids a first-paint flash where images
  // render at the CSS fallback height and then jump to the computed value.
  applyUniformProductImageHeight();

  $(".variant_total_qty").each(function () {
    const text = $(this).text().trim();
    if (text && text !== "-") {
      $(this).text(formatNumber(text));
    }
  });

  initThumbnailScrolling();
  initDragToScroll();
  initZipCodeHandler();

  // Registers window.WPOpenProductLightbox for theme.js's openGallery handler.
  // Independent of warehouse data, so it goes in immediately rather than in the
  // deferred waitForWarehouseData block below.
  initProductLightbox();

  // The cart page's free-promotion panel and its watcher (a no-op on every other
  // page — it looks for [data-promo-root]). Needs no warehouse data.
  void initPromotions();

  $(document).on("click", ".Product_swatch_colors .swatch-option", function () {
    reinitThumbnailScrolling();
  });

  // Warehouse (i) info button — open modal with shipping details
  $(document).on("click", ".warehouse-info-btn", function (e: JQuery.ClickEvent) {
    e.stopPropagation();
    const { warehouseName, orderBy, delivery, daysText } =
      getWarehouseInfoDetails(this as HTMLElement);

    const $modal = $("#warehouse-info-modal");
    $modal.find(".warehouse-info-modal-name").text(warehouseName);

    let bodyHtml = "";
    if (daysText) bodyHtml += `<div>${daysText}</div>`;
    if (orderBy) bodyHtml += `<div>Order by: ${orderBy}</div>`;
    if (delivery) bodyHtml += `<div>Est. Delivery: ${delivery}</div>`;
    if (!bodyHtml) bodyHtml = "<div>Shipping information unavailable.</div>";

    $modal.find(".warehouse-info-modal-body").html(bodyHtml);
    $modal.addClass("is-visible");
  });

  // Close modal on overlay click or close button
  $(document).on("click", ".warehouse-info-modal-overlay, .warehouse-info-modal-close", function () {
    $("#warehouse-info-modal").removeClass("is-visible");
  });

  // Close modal on Escape key
  $(document).on("keydown", function (e: JQuery.KeyDownEvent) {
    if (e.key === "Escape") {
      $("#warehouse-info-modal").removeClass("is-visible");
    }
  });

  setTimeout(function () {
    loadTransitTimes();
  }, 500);

  setTimeout(function () {
    waitForWarehouseData(function () {
      // Initialize lazy product loader (must run before initSingleColorMode and
      // prePopulateFromCart so the family order and loaded-set are ready)
      initLazyProductLoader({
        onProductLoaded: (handle, $rows) => {
          $rows.each(function () {
            const $row = $(this);
            const warehouseCode = $row.attr("data-warehouse-code") ?? "WPBR";

            // Copy transit-time HTML from an already-loaded row with the same
            // warehouse code. Transit times are warehouse-based, not color-based,
            // so the computed value is identical for every product in the family.
            const $transitInfo = $row.find(".transit-info");
            if ($transitInfo.length > 0) {
              const $reference = $(
                `.warehouse_row[data-warehouse-code="${warehouseCode}"] .transit-info`,
              ).not($transitInfo[0]).filter(function () {
                return $(this).find(".transit-loading").length === 0;
              }).first();

              if ($reference.length > 0) {
                $transitInfo.html($reference.html());
                // Carry over modal data and tooltip stored on the reference
                const orderBy = $reference.data("transit-order-by") as string | undefined;
                const delivery = $reference.data("transit-delivery") as string | undefined;
                const daysText = $reference.data("transit-days-text") as string | undefined;
                if (orderBy) $transitInfo.data("transit-order-by", orderBy);
                if (delivery) $transitInfo.data("transit-delivery", delivery);
                if (daysText) $transitInfo.data("transit-days-text", daysText);
                const tooltipText = $reference.closest(".warehouse_row")
                  .find(".warehouse-info-btn").attr("data-tooltip");
                if (tooltipText) $row.find(".warehouse-info-btn").attr("data-tooltip", tooltipText);
                // Also carry over the estimated-delivery data attribute used by
                // the availability filter
                const estDelivery = $reference
                  .closest(".warehouse_row")
                  .attr("data-estimated-delivery");
                if (estDelivery) {
                  $row.attr("data-estimated-delivery", estDelivery);
                }
              }
            }
          });

          // Re-apply the current availability filter so newly inserted rows
          // receive show-this-warehouse (or not) based on their delivery date.
          reapplyCurrentFilter();

          // Newly inserted rows contain .inventory_submit buttons — sync their
          // disabled state with global dirty tracking. Baseline for new rows
          // defaults to "" so they register as clean until the user types.
          updateButtonStates();

          // Tell the mobile mirror module that a new product's rows + card
          // are in the DOM. Listener is a no-op on desktop (it short-circuits
          // when there are no mobile cards). Dispatched after filter
          // reapplication so the mirror reads the final visibility classes.
          document.dispatchEvent(
            new CustomEvent("product:lazy-row-inserted", {
              detail: { handle, rows: $rows.toArray() },
            }),
          );
        },
      });

      // Capture canonical row order before anything can reorder the tbody.
      // Both singleColorMode and colorPin read this list, so it has to be
      // taken while the DOM is still in server order.
      initColorPin();

      // Initialize pricing grid components after warehouse data loads
      initPricingGrid();
      initInputValidation();
      initAddToCartHandler();

      // Initialize filter and gallery components (placeholders for now)
      initColorFilter();
      initAvailabilityFilter();
      initSwatchGallery();
      initResetFilters();
      initSingleColorMode();
      initNavigationGuard();
      initBackToTopHandler();

      // Mobile mirror + in-card Update Cart button. Both are mobile-only at
      // runtime (matchMedia gates) but their event listeners are bound here
      // so they pick up the canonical inputs as soon as those are wired.
      initMobileMirror();
      initMobileCardSubmit();

      // When cart sync applies a remote change after the grid is already built,
      // re-run the same chain so inputs, mobile mirrors and button states
      // reflect the new cart — then re-baseline EXACTLY the rows the apply
      // touched.
      //
      // That last step is load-bearing. prePopulateFromCart only writes
      // input.value; it never updates dirtyTracking. Without an explicit
      // re-baseline every applied row stays dirty forever, which keeps the
      // Update Cart buttons lit, fires the leave-page guard on every link, and
      // — because a dirty row is held back from the next apply — freezes
      // lastAppliedVersion so the client re-applies the same version every
      // poll. captureBaseline() cannot do this: it never overwrites an existing
      // entry. setBaselinesFromSnapshot() cannot either: it clears the map and
      // would mark the user's own unsaved edits clean.
      registerAfterApplyHook(async function (appliedKeys) {
        await prePopulateFromCart();
        syncAllMirrorsFromCanonical();
        markCartPrepopulatedRows();

        if (appliedKeys.size > 0) {
          const current = snapshotQuantityValues();
          const applied = new Map<string, string>();
          appliedKeys.forEach(function (key) {
            applied.set(key, current.get(key) ?? "");
          });
          setBaselineForKeys(applied);
        }

        updateButtonStates();
      });

      // Pre-populate grid from cart, then mark rows that received cart quantities,
      // then activate single color mode as the default starting state
      prePopulateFromCart()
        .then(function () {
          // Push pre-populated cart values into mobile mirror inputs so
          // cards reflect the same quantities as the canonical table on
          // first paint (cart prePop writes input.value directly without
          // dispatching events, which is why an explicit sync is needed).
          syncAllMirrorsFromCanonical();
          markCartPrepopulatedRows();
          // Capture baseline *after* cart pre-population writes its values so
          // pre-populated quantities register as the clean starting state.
          // Rows that load lazily later start empty and default to baseline ""
          // via isAnyDirty's fallback — no further capture needed.
          captureBaseline();
          return activateInitialSingleColorMode();
        })
        .then(function () {
          // Before scrollToVariantFromUrl so the cart's flagged scroll measures
          // the row in its final position.
          return pinDeepLinkedColor();
        })
        .then(function () {
          void scrollToVariantFromUrl();
          // Re-evaluate button state after any rows activateInitialSingleColorMode
          // may have inserted (lazy-loaded).
          updateButtonStates();
          // Pin every Update Cart button to a single uniform height based on
          // the first product's rowspan. Runs once — by design we don't
          // recompute when rows are later hidden/shown by filters.
          applyUniformUpdateCartButtonHeight();
          // Mark the grid ready: hide loader, swap loading→ready class,
          // and reveal the mobile_table_wrapper. Replaces the previous
          // 50ms fixed timeout — readiness is now event-driven.
          markProductGridReady();
          // The mobile in-card Update Cart buttons read dirty state to
          // decide enabled state; do a final refresh after pre-pop + baseline
          // are settled so they show the correct labels on first paint.
          refreshMobileCardSubmit();
        })
        .catch(function (error: unknown) {
          console.error("Failed to pre-populate cart:", error);
          // Even on failure, surface the grid so the user isn't stuck
          // staring at the loader forever.
          markProductGridReady();
        });
    });
  }, 400);
});

export { formatNumber, formatPrice, showCustomPopup, waitForWarehouseData, getWarehouseByCode };
